import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { FileText } from 'lucide-react';

interface LateFeesViewProps {
  dateFilter: string;
  utilityTypeFilter: string;
  locationFilter: string;
}

interface LateFeeRow {
  pearId: string;
  netLateFee: number;
  totalCharges: number;
  lateFeePercent: number;
  utilityType: string;
  vendorName: string;
}

const generateDummyData = (): LateFeeRow[] => {
  return [
    { pearId: '1031510', netLateFee: 56.48, totalCharges: 678.35, lateFeePercent: 8.33, utilityType: 'SEWER', vendorName: 'City of Reading Water' },
    { pearId: '1031511', netLateFee: 195.76, totalCharges: 1912.55, lateFeePercent: 10.24, utilityType: 'SEWER', vendorName: 'City of Reading Water' },
    { pearId: '1031512', netLateFee: 45.32, totalCharges: 543.20, lateFeePercent: 8.34, utilityType: 'SEWER', vendorName: 'City of Reading Water' },
    { pearId: '1031513', netLateFee: 38.50, totalCharges: 462.00, lateFeePercent: 8.33, utilityType: 'ELECTRIC', vendorName: 'Duke Energy' },
    { pearId: '1031514', netLateFee: 28.75, totalCharges: 345.00, lateFeePercent: 8.33, utilityType: 'WATER', vendorName: 'Greater Cincinnati Water Works' },
    { pearId: '1031515', netLateFee: 15.20, totalCharges: 182.40, lateFeePercent: 8.33, utilityType: 'REFUSE', vendorName: 'Rumpke' },
  ];
};

const utilityTypeColors: Record<string, string> = {
  'SEWER': '#1e40af',
  'ELECTRIC': '#3b82f6',
  'WATER': '#06b6d4',
  'REFUSE': '#10b981',
  'NATURALGAS': '#16a34a',
  'LIGHTING': '#84cc16',
  'FIREPROTECTION': '#f59e0b',
  'STORMWATER': '#9ca3af',
};

export function LateFeesView({
  dateFilter,
  utilityTypeFilter,
  locationFilter,
}: LateFeesViewProps) {
  const [viewMode, setViewMode] = useState<'donut' | 'table'>('donut');
  const [breakdownBy, setBreakdownBy] = useState<'utilities' | 'vendors' | 'locations'>('utilities');

  const dummyData = generateDummyData();

  // Calculate totals by utility type
  const utilityTotals = dummyData.reduce((acc, row) => {
    if (!acc[row.utilityType]) {
      acc[row.utilityType] = { total: 0, count: 0 };
    }
    acc[row.utilityType].total += row.netLateFee;
    acc[row.utilityType].count += 1;
    return acc;
  }, {} as Record<string, { total: number; count: number }>);

  const pieData = Object.entries(utilityTotals).map(([name, data]) => ({
    name,
    value: parseFloat(data.total.toFixed(2)),
    count: data.count,
  }));

  const totalLateFees = dummyData.reduce((sum, row) => sum + row.netLateFee, 0);

  return (
    <div className="space-y-4">
      {/* View Selection */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select to View:</label>
                <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'donut' | 'table')}>
                  <TabsList>
                    <TabsTrigger value="donut">Donut Chart</TabsTrigger>
                    <TabsTrigger value="table">Tabular Data</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Breakdown by:</label>
                <Tabs value={breakdownBy} onValueChange={(v) => setBreakdownBy(v as 'utilities' | 'vendors' | 'locations')}>
                  <TabsList>
                    <TabsTrigger value="utilities">Utilities</TabsTrigger>
                    <TabsTrigger value="vendors">Vendors</TabsTrigger>
                    <TabsTrigger value="locations">Locations</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Donut Chart and Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {viewMode === 'donut' && (
          <Card>
            <CardHeader>
              <CardTitle>Utility Type</CardTitle>
              <CardDescription>Late fees breakdown by utility type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => 
                        `${(percent * 100).toFixed(1)}%`
                      }
                      outerRadius={100}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={utilityTypeColors[entry.name] || '#8884d8'} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [`$${value.toFixed(2)}`, 'Late Fee']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2">
                {pieData.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded" 
                        style={{ backgroundColor: utilityTypeColors[entry.name] || '#8884d8' }}
                      />
                      <span>{entry.name}</span>
                    </div>
                    <span className="font-semibold">
                      ${entry.value.toFixed(2)} ({(entry.value / totalLateFees * 100).toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className={viewMode === 'donut' ? '' : 'lg:col-span-2'}>
          <CardHeader>
            <CardTitle>Late Fees Details</CardTitle>
            <CardDescription>To see the bill, click on the Pear ID</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pear ID</TableHead>
                    <TableHead>Net Late Fee</TableHead>
                    <TableHead>Total Charges</TableHead>
                    <TableHead>Late fee/charges</TableHead>
                    <TableHead>Utility Type</TableHead>
                    <TableHead>Vendor Name</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dummyData.map((row) => (
                    <TableRow key={row.pearId}>
                      <TableCell>
                        <Button
                          variant="link"
                          className="p-0 h-auto font-semibold text-primary"
                          onClick={() => {
                            // Navigate to bill detail
                            console.log('Navigate to bill:', row.pearId);
                          }}
                        >
                          {row.pearId}
                        </Button>
                      </TableCell>
                      <TableCell>${row.netLateFee.toFixed(2)}</TableCell>
                      <TableCell>${row.totalCharges.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant={row.lateFeePercent > 10 ? 'destructive' : 'secondary'}>
                          {row.lateFeePercent.toFixed(2)}%
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.utilityType}</Badge>
                      </TableCell>
                      <TableCell>{row.vendorName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

